// LAP-550 — Design tokens partagés pour l'app Investissements
// Centralise couleurs, gradients, ombres et rayons pour une cohérence visuelle.

export const invTheme = {
  // Surface
  cardBg:
    'linear-gradient(155deg, rgba(7,40,82,1) 0%, rgba(0,27,66,1) 55%, rgba(0,18,44,1) 100%)',
  cardBorder: '1px solid rgba(255,255,255,0.06)',
  cardBorderHover: '1px solid rgba(99,160,255,0.35)',
  cardShadow: '0 4px 18px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
  cardRadius: 14,

  // Accent par catégorie (utilisé pour la liseré gauche)
  accents: {
    portfolio: '#63a0ff',
    advice: '#a78bfa',
    repartition: '#22d3ee',
    target: '#34d399',
    opportunity: '#f59e0b',
    market: '#60a5fa',
    commodities: '#fbbf24',
    realEstate: '#f97316',
    alternative: '#c084fc',
    startups: '#f472b6',
    forex: '#38bdf8',
  },

  // Typographie
  title: { fontSize: 18, fontWeight: 600, color: '#ffffff', letterSpacing: 0.2 },
  subtitle: { fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.3, textTransform: 'uppercase' },
  value: { fontSize: 20, fontWeight: 700, color: '#ffffff' },
  meta: { fontSize: 12, color: 'rgba(255,255,255,0.55)' },

  // États
  positive: '#22c55e',
  negative: '#ef4444',
  positiveSoft: 'rgba(34,197,94,0.12)',
  negativeSoft: 'rgba(239,68,68,0.12)',
  neutralSoft: 'rgba(255,255,255,0.06)',

  // Tableaux
  rowDivider: '1px solid rgba(255,255,255,0.05)',
  rowHover: 'rgba(255,255,255,0.04)',
}

export const sharedDataGridSx = {
  border: 'unset',
  color: 'white',
  fontSize: 13,
  borderRadius: 2,
  overflow: 'hidden',
  '.MuiDataGrid-columnHeaders': {
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.65)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: 11,
  },
  '.MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
  '.MuiDataGrid-columnSeparator': { display: 'none' },
  '.MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  '.MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
  '.MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(99,160,255,0.10)',
    '&:hover': { backgroundColor: 'rgba(99,160,255,0.15)' },
  },
  // Footer / pagination : dark theme aligné sur le reste
  '.MuiDataGrid-footerContainer': {
    background: 'transparent',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.7)',
    minHeight: 44,
  },
  '.MuiTablePagination-root': {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
  },
  '.MuiTablePagination-toolbar': {
    alignItems: 'center',
    minHeight: 44,
  },
  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    margin: 0,
  },
  '.MuiTablePagination-select': {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 12,
  },
  '.MuiTablePagination-actions .MuiIconButton-root': {
    color: 'rgba(255,255,255,0.7)',
    '&:hover': { color: '#63a0ff', backgroundColor: 'rgba(99,160,255,0.08)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
  },
  '.MuiCheckbox-root': { color: 'rgba(255,255,255,0.6)' },
  '.Mui-checked': { color: '#63a0ff !important' },
  '.MuiSvgIcon-root': { fill: 'currentColor' },
  // Scrollbar discrète à la place des scrollbars natives blanches
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar': { width: 8, height: 8 },
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 4,
  },
  '.MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(99,160,255,0.4)',
  },
  // Barre d'outils (recherche / colonnes / filtres) — LAP-551
  '.MuiDataGrid-toolbarContainer': {
    gap: 1,
    padding: '0 0 8px 0',
    '.MuiButton-root': {
      color: 'rgba(255,255,255,0.7)',
      textTransform: 'none',
      fontSize: 12,
      '&:hover': { color: '#63a0ff', backgroundColor: 'rgba(99,160,255,0.08)' },
    },
  },
  // Le panneau de filtres du DataGrid est monté dans le grid : sans ces
  // règles il hérite du texte blanc sur fond blanc et devient illisible.
  '.MuiDataGrid-panelWrapper .MuiFormLabel-root': { color: 'rgba(0,0,0,0.6)' },
  '.MuiDataGrid-panelWrapper .MuiInputBase-root': { color: 'rgba(0,0,0,0.87)' },
}

/** Champ de recherche des barres d'outils de tableau (thème sombre). */
export const searchFieldSx = {
  minWidth: 220,
  '.MuiOutlinedInput-root': {
    color: 'white',
    fontSize: 13,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(99,160,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#63a0ff' },
  },
  '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
  input: { '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 } },
}
